import { useTranslation } from 'react-i18next';

import { cn } from '~/utils/cn';

import { ExperimentStatus } from '../types/experiment.types';

/** Status → token class pair. Covers all 6 lifecycle states per the
 *  State-Transitions reference. Fine-grained "where in the journey" is read
 *  from `experimentPhase` and the auxiliary workflow-status fields. */
const STATUS_STYLE: Record<ExperimentStatus, { className: string; labelKey: string }> = {
  [ExperimentStatus.DRAFT]: {
    className: 'bg-info-50 text-info',
    labelKey: 'status.draft',
  },
  [ExperimentStatus.CREATED]: {
    className: 'bg-exp-status-created-bg text-exp-status-created',
    labelKey: 'status.created',
  },
  [ExperimentStatus.RUNNING]: {
    className: 'bg-exp-status-running-bg text-exp-status-running',
    labelKey: 'status.running',
  },
  [ExperimentStatus.COMPLETED]: {
    className: 'bg-success-50 text-success',
    labelKey: 'status.completed',
  },
  [ExperimentStatus.CANCELLED]: {
    className: 'bg-warning-50 text-warning',
    labelKey: 'status.cancelled',
  },
  /** Operator declined at the approval gate — distinct from CANCELLED (POST /stop). */
  [ExperimentStatus.REJECTED]: {
    className: 'bg-destructive/10 text-destructive',
    labelKey: 'status.rejected',
  },
  /** Pipeline step failed (e.g. full-catalog generation, delivery). */
  [ExperimentStatus.FAILED]: {
    className: 'bg-destructive/10 text-destructive',
    labelKey: 'status.failed',
  },
};

interface ExperimentStatusChipProps {
  status: ExperimentStatus;
  className?: string;
  /** E2E `data-testid`; the raw status enum is exposed on `data-status`. */
  testId?: string;
}

/** Text-only experiment status pill. Width grows with the label per spec
 *  (Figma fixes 120×36 but content lengths vary); padding + radius track
 *  the design tokens. */
export function ExperimentStatusChip({
  status,
  className,
  testId = 'experiment-status-chip',
}: ExperimentStatusChipProps) {
  const { t } = useTranslation('experiments');
  const cfg = STATUS_STYLE[status];
  return (
    <span
      data-testid={testId}
      data-status={status}
      className={cn(
        'inline-flex h-9 items-center justify-center rounded-lg px-3 py-2 text-sm font-medium',
        cfg.className,
        className,
      )}
    >
      {t(cfg.labelKey)}
    </span>
  );
}
